'use client';

import { MarketId } from '@drift/common';
import useMarketsInfoStore, {
	MarketsInfoStore,
} from 'src/stores/useMarketsInfoStore';
import {
	AMM_RESERVE_PRECISION,
	BASE_PRECISION,
	PerpMarketAccount,
	SpotMarketAccount,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { PerpMarketInfo, SpotMarketInfo } from 'src/stores/types';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useSpotMarketsSortedByMarketIndex } from './spotMarkets/useSpotMarketsSortedByMarketIndex';

const DEFAULT_STEP_SIZE = 0.1;

const getStepSizeFromPerpMarketAccount = (account: PerpMarketAccount) => {
	const stepSizeBn = account?.amm?.orderStepSize ?? AMM_RESERVE_PRECISION;
	return stepSizeBn.toNumber() / AMM_RESERVE_PRECISION.toNumber();
};

const getStepSizeFromSpotMarketAccount = (
	account: SpotMarketAccount,
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	if (!account) return DEFAULT_STEP_SIZE;

	return (
		account.orderStepSize.toNumber() /
		spotMarketsSortedByMarketIndex[account.marketIndex].precision.toNumber()
	);
};

export const getMarketStepSize = (
	marketId: MarketId,
	marketInfoStoreGetter: MarketsInfoStore['get'],
	spotMarketsSortedByMarketIndex: SpotMarketConfig[]
) => {
	const selectedMarketInfo = marketId
		? marketInfoStoreGetter().getMarketInfoByIndexAndType(
				marketId.marketIndex,
				marketId.marketType
			)
		: undefined;

	let stepSize = DEFAULT_STEP_SIZE;

	if (!selectedMarketInfo) {
		return DEFAULT_STEP_SIZE;
	}

	if (marketId.isPerp) {
		stepSize = getStepSizeFromPerpMarketAccount(
			(selectedMarketInfo as PerpMarketInfo).account
		);
	} else {
		stepSize = getStepSizeFromSpotMarketAccount(
			(selectedMarketInfo as SpotMarketInfo).account,
			spotMarketsSortedByMarketIndex
		);
	}

	return stepSize;
};

// Returns the step size of a market. e.g. SOL-PERP has a step size of 0.1
export const useMarketStepSize = (marketId: MarketId | undefined) => {
	const storeGetter = useMarketsInfoStore((s) => s.get);
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	if (!marketId) return DEFAULT_STEP_SIZE;
	return getMarketStepSize(
		marketId,
		storeGetter,
		spotMarketsSortedByMarketIndex
	);
};

export const useCurrentMarketStepSize = () => {
	const currentlySelectedMarket = useDriftStore(
		(s) => s.selectedMarket.current
	);
	return useMarketStepSize(currentlySelectedMarket?.marketId);
};

const getMarketStepSizeBn = (
	marketId: MarketId,
	marketInfoStoreGetter: MarketsInfoStore['get']
) => {
	const selectedMarketInfo = marketId
		? marketInfoStoreGetter().getMarketInfoByIndexAndType(
				marketId.marketIndex,
				marketId.marketType
			)
		: undefined;

	if (!selectedMarketInfo) {
		return BASE_PRECISION;
	}

	if (marketId.isPerp) {
		return (selectedMarketInfo as PerpMarketInfo).account?.amm?.orderStepSize;
	} else {
		return (selectedMarketInfo as SpotMarketInfo)?.account?.orderStepSize;
	}
};

const useMarketStepSizeBn = (marketId: MarketId | undefined) => {
	const storeGetter = useMarketsInfoStore((s) => s.get);

	if (!marketId) {
		return BASE_PRECISION;
	}

	return getMarketStepSizeBn(marketId, storeGetter);
};

export const useCurrentMarketStepSizeBn = () => {
	const currentlySelectedMarket = useDriftStore(
		(s) => s.selectedMarket.current
	);

	return useMarketStepSizeBn(currentlySelectedMarket?.marketId);
};
